<template>
  <div>
    <div class="tw:overflow-x-hidden">
      <AuthMenu isLanding @onRegisterClick="onAuthorize('login')" pageName="rate-connect" />
      <RateConnectHero
        v-scroll-animate="{ duration: '2s', delay: '0.3s' }"
        pageName="rate-connect"
        @onLoginClick="onAuthorize('login')"
      />
      <RateConnectBenefits v-scroll-animate="{ duration: '2s', delay: '0.3s' }" class="tw:bg-white" />
      <LandingPageProductsComparison
        v-scroll-animate="{ duration: '2s', delay: '0.3s' }"
        title="tbs_product_header"
        product="rate-connect"
        @onLoginClick="onAuthorize('login')"
      />
      <RateConnectReviews
        v-scroll-animate="{ duration: '2s', delay: '0.3s' }"
        class="tw:bg-white tw:py-6 tw:lg:px-0 tw:lg:py-8"
        :withHeader="true"
        :withReadMore="false"
        :isLandingPage="true"
      />
      <RateConnectStart v-scroll-animate="{ duration: '2s', delay: '0.3s' }" @onLoginClick="onAuthorize('login')" />
      <div v-if="shouldShowSmartBoost">
        <div class="tw:bg-white tw:h-6" v-scroll-animate="{ duration: '2s', delay: '0.3s' }" />
        <RateConnectSmartBoost
          v-scroll-animate="{ duration: '2s', delay: '0.3s' }"
          class="tw:bg-blue-100"
          @openVideo="openVideo"
        />
      </div>
      <RateConnectConnectivityProviders
        :withHeader="true"
        v-scroll-animate="{ duration: '2s', delay: '0.3s' }"
        class="tw:bg-white"
      />
      <ProductPageFaq
        v-scroll-animate="{ duration: '2s', delay: '0.3s' }"
        pageName="rate-connect"
        class="tw:bg-white"
      />
    </div>
    <ThePageFooter isLanding />
    <ChatWidget />
    <SmartBoostVideoModal :isOpen="isVideoOpen" @closeModal="closeVideo" :title="$t('rc_smartboost_video_header')" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import appAnalytics from '@/utils/tracking'
import AuthMenu from '@/components/landingPage/AuthMenu.vue'
import RateConnectHero from '@/components/productPage/rateConnect/RateConnectHero.vue'
import RateConnectBenefits from '@/components/productPage/rateConnect/RateConnectBenefits.vue'
import LandingPageProductsComparison from '@/components/landingPage/LandingPageProductsComparison.vue'
import ProductPageFaq from '@/components/productPage/ProductPageFaq.vue'
import RateConnectReviews from '@/components/productPage/rateConnect/RateConnectReviews.vue'
import ThePageFooter from '@/components/page/ThePageFooter.vue'
import RateConnectStart from '@/components/productPage/rateConnect/RateConnectStart.vue'
import RateConnectConnectivityProviders from '@/components/productPage/rateConnect/RateConnectConnectivityProviders.vue'
import ChatWidget from '@/components/chatbot/ChatWidget.vue'
import RateConnectSmartBoost from '@/components/productPage/rateConnect/RateConnectSmartBoost.vue'
import SmartBoostVideoModal from '@/components/rateConnect/smartBoost/SmartBoostVideoModal.vue'

import type { AuthType, AuthData } from '@/types'

const store = useStore()
const route = useRoute()

/** Feature flag for SmartBoost visibility */
const shouldShowSmartBoost = import.meta.env.VITE_FEATURE_VISIBLE === 'true'

/** Current window width for responsive behavior */
const windowWidth = ref<number>(window.innerWidth)

/** Computed locale from store */
const locale = computed(() => store.state.locale)

/** Video modal state */
const isVideoOpen = ref<boolean>(false)

/**
 * Open SmartBoost video modal
 */
const openVideo = (): void => {
  isVideoOpen.value = true
}

/**
 * Close SmartBoost video modal
 */
const closeVideo = (): void => {
  isVideoOpen.value = false
}

/**
 * Handle window resize event
 */
const onResize = (): void => {
  windowWidth.value = window.innerWidth
}

/**
 * Dispatch authorization action to store
 */
const authorize = (authData: AuthData): Promise<void> => store.dispatch('session/authorize', authData)

/**
 * Handle authorization click (login or register)
 * @param type - Auth type (login or register)
 */
const onAuthorize = async (type: AuthType = 'login'): Promise<void> => {
  appAnalytics.track(appAnalytics.events.BUTTON_CLICKED, {
    button: type,
    property_id: null,
  })

  await authorize({
    type,
    locale: locale.value.language,
    stateParams: {
      redirectFrom: route.query['redirectFrom'],
    },
  })
}

// ==================== Lifecycle ====================

onMounted(() => {
  nextTick(() => {
    window.addEventListener('resize', onResize)
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<style scoped>
.terms-body :deep(p) {
  margin-bottom: 16px;
}
.terms-body :deep(a) {
  color: #007bff;
}
.terms-body :deep(h3) {
  font-weight: bold;
  margin-bottom: 16px;
  font-size: 28px;
  line-height: 35px;
  margin-top: 32px;
}
.terms-body :deep(#exhibit-1) {
  padding-top: 64px;
}

.terms-body :deep(#exhibit-2) {
  padding-top: 64px;
}
</style>
